import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import WizardStepper from 'components/generic/stepper/WizardStepper'
import { getFormOptions } from 'utils/validation/customer'
import { ACCOUNT_TYPES, CHURN_ZERO_EVENTS as cz, RETAIL_SUPPLIER_TYPE_ID } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { dispatchSnackbar } from 'utils/helpers'
import { capitalize, makeCommaSeparatedString } from 'utils/data-manipulation/valueFormatter'
import {
  useArchiveCustomerMutation,
  useCreateCustomerMutation,
  useGetCustomerQuery,
  useUpdateCustomerMutation,
  useCheckBuyEligibilityMutation,
} from 'services/customers'
import Loading from 'components/generic/Loading'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import { setSnackBar } from 'state/ui'
import { setCarrier, setCustomer } from 'state/customer'
import { clearContact } from 'state/customer/customerContactsSlice'
import VehiclesForm from '../vehicles/VehiclesForm'
import CustomerGeneralInfoForm from '../account/CustomerGeneralInfoForm'
import LocationsInformationForm from '../locations/LocationsInformationForm'
import ContactsForm from '../contacts/ContactsForm'
import ComplianceInformationForm from '../contacts/ComplianceInformationForm'
import AddressDefaultsForm from '../address-defaults/AddressDefaultsForm'
import {
  accountTypesArray,
  formatCustomer,
  formatContact,
  formatData,
  getDefaultValues,
  isDirtyFieldInContacts,
} from './utils'
import SupplierForm from '../contacts/SupplierForm'
import FormattedRadioGroup from '../../../generic/form/FormattedRadioGroup'
import CustomerAdditionalInformationForm from '../account/CustomerAdditionalInformationForm'
import FinancialInformationForm from '../financial-information/FinancialInformationForm'
import { useSaveGsPayVendorMutation } from 'services/payabli'
import CarrierGeneralInfoForm from '../account/CarrierGeneralInfoForm'
import useDuplicateCustomerCheck from 'utils/custom-hooks/useDuplicateCustomerCheck.jsx'
import { useGetDriversQuery } from 'services/drivers'

const NewCustomerContainer = ({ onFormStateChange, onHideModal }) => {
  const { customerId, type, hideAll, licenseData } = useSelector(state => state.ui.formModalProps)
  const closeAfterSave = ['locations', 'contacts'].includes(type)

  const defaultYardCurrency = useSelector(state => state.settings.defaultCurrency)
  const isUpdate = !!customerId
  const initialValues = getDefaultValues()
  const [isArchiveModalUp, setIsArchiveModalUp] = useState(false)
  const navigate = useNavigate()

  const { enableAddressPrintingUpdates } = useSelector(state => state.featureFlags?.variables || {})
  const defaultValues = {
    ...initialValues,
    isSupplier: type === 'supplier',
    accountType:
      (['supplier', 'purchaseOrder', 'contacts', undefined].includes(type) && ACCOUNT_TYPES.SUPPLIER.id) ||
      (['carrier'].includes(type) && ACCOUNT_TYPES.CARRIER.id) ||
      ACCOUNT_TYPES.CONSUMER.id,
    supplierTypeId: type === 'supplier' ? RETAIL_SUPPLIER_TYPE_ID : '',
    locations: type === 'supplier' ? [] : initialValues.locations,
    defaultCurrency: defaultYardCurrency,
    driverIds: [],
    // Merge in license data if provided
    ...(licenseData || {}),
  }
  const methods = useForm({
    mode: 'onTouched',
    defaultValues,
    resolver: getFormOptions(type === 'supplier'),
  })

  const { handleSubmit, control, formState, watch, getValues, setValue, reset, trigger } = methods

  const accountTypeValue = watch('accountType')
  const isCarrier = accountTypeValue === ACCOUNT_TYPES.CARRIER.id
  const { errors, isDirty, dirtyFields, isValid } = formState
  const [showCI, setShowCI] = useState(false)
  const [contactIndex, setContactIndex] = useState('')
  const [initialStep, setInitialStep] = useState(
    (['contacts'].includes(type) && 2) || (['locations', 'carrier', 'consumer'].includes(type) && 1) || 0,
  )
  const [title, setTitle] = useState(
    `Add New ${
      (['purchaseOrder'].includes(type) && 'Supplier') ||
      (['salesOrder', 'consumer'].includes(type) && 'Consumer') ||
      (['carrier'].includes(type) && 'Carrier') ||
      'Customer'
    }`,
  )
  const dispatch = useDispatch()
  const [originalCustomer, setOriginalCustomer] = useState(null)
  const [hasDoNotBuyContacts, setHasDoNotBuyContacts] = useState(false)

  const duplicateCheckResult = useDuplicateCustomerCheck({
    onCloseModal: () => {
      // No-op, modal is closed internally
    },
    onUseExistingCustomer: selected => {
      if (type === 'supplier') {
        dispatch(setCustomer(selected))
      }
      onHideModal()
    },
  })

  const { checkDuplicateCustomer, DuplicateCustomerModal, isLoadingCustomers } = isUpdate
    ? {
        checkDuplicateCustomer: () => {},
        DuplicateCustomerModal: () => null,
        isLoadingCustomers: false,
      }
    : duplicateCheckResult

  const handleSaveCI = data => {
    setValue(`contacts.${contactIndex}`, data, { shouldDirty: true })
    if (!getValues(`contacts.${contactIndex}.firstName`)) {
      setValue(`contacts.${contactIndex}.firstName`, data.firstNameLegal, { shouldDirty: true })
    }
    if (!getValues(`contacts.${contactIndex}.lastName`)) {
      setValue(`contacts.${contactIndex}.lastName`, data.lastNameLegal, { shouldDirty: true })
    }
    setShowCI(false)
    setContactIndex('')
  }
  const handleShowCI = cIndex => {
    setContactIndex(cIndex)
    setShowCI(true)
  }

  const [createCustomer, { isLoading: isCreatingCustomer }] = useCreateCustomerMutation()

  const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation()
  const { data: customerData, isLoading: isGettingData } = useGetCustomerQuery(
    { customerId },
    { refetchOnMountOrArgChange: true, skip: !isUpdate },
  )
  const [archiveCustomer, { isLoading: isLoadingArchiveCustomer }] = useArchiveCustomerMutation()

  const [checkBuyEligibility, { isLoading: isLoadingCheckBuyEligibility }] = useCheckBuyEligibilityMutation()

  // Initialize the saveGsPayVendor mutation hook
  const [saveGsPayVendor, { isLoading: isSavingGsPayVendor }] = useSaveGsPayVendorMutation()

  const { data: availableDriversData, isFetching: isFetchingAvailableDriversData } = useGetDriversQuery({
    filter: JSON.stringify({ carrierId: null }),
  })

  const driversOptionList = useMemo(() => {
    if (!availableDriversData?.Items || !customerData) return []

    const combinedDrivers = [
      ...availableDriversData.Items.map(driver => ({
        id: driver.id,
        name: driver.name,
      })),
      ...customerData.drivers.map(driver => ({
        id: driver.id,
        name: driver.name,
      })),
    ]

    return combinedDrivers.sort((a, b) => a.name.localeCompare(b.name))
  }, [availableDriversData, customerData])

  const updateCustomersFilter = () => {
    if (isUpdate) return // Skip duplicate check for existing customers

    const companyNameValue = getValues('companyName')
    const contacts = getValues('contacts')
    const supplier = contacts[0]
    const { firstNameLegal, lastNameLegal } = supplier
    const search = companyNameValue || `${firstNameLegal} ${lastNameLegal}`
    if (search) {
      checkDuplicateCustomer(search.trim())
    }
  }

  const handleSaveGsPayVendor = async contactId => {
    // Skip if no contact ID or if it's the same as the original
    if (!contactId || (originalCustomer && originalCustomer.gsPayContactId === contactId)) {
      return null
    }

    try {
      const vendorResponse = await saveGsPayVendor({
        saveGsPayVendorRequest: {
          contactId: contactId,
        },
      }).unwrap()

      return vendorResponse.externalVendorId
    } catch (error) {
      dispatch(setSnackBar({ snackbarType: 'error', snackbarMessage: 'Could not save GreenSpark Pay vendor' }))
      throw error
    }
  }

  useEffect(() => {
    if (customerData) {
      setTitle(`Edit "${customerData.customerCommonIdentifierString || 'customer'}"`)
      const customerFormatted = formatCustomer(initialValues, customerData)
      setOriginalCustomer(customerFormatted)
      reset(customerFormatted)
    }
  }, [customerData])

  const handleError = error =>
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || `There was an error ${isUpdate ? 'updating' : 'creating'} the customer`,
      }),
    )

  const handleSuccess = () =>
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `${isCarrier ? 'Carrier' : 'Customer'} successfully ${isUpdate ? 'updated' : 'created'}`,
      }),
    )

  const startStep = [
    {
      label: 'Start',
      showStepper: false,
      stepOptional: false,
      footerButtonTitles: ['CONTINUE', 'CANCEL'],
      contentComponent: (
        <FormattedRadioGroup
          control={control}
          name="accountType"
          title="What type of customer are you adding?"
          items={accountTypesArray}
          error={!!errors.accountType}
        />
      ),
    },
  ]

  const steps = [
    {
      label: 'General Information',
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: ['NEXT: LOCATIONS', 'BACK TO START'],
      contentComponent: (
        <CustomerGeneralInfoForm getValues={getValues} control={control} errors={errors} isUpdate={isUpdate} />
      ),
    },
    {
      label: 'Locations',
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: ['NEXT: CONTACTS AND COMPLIANCE', 'BACK'],
      contentComponent: (
        <LocationsInformationForm
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
        />
      ),
    },
    {
      label: 'Contacts and Compliance',
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: [`NEXT: ${enableAddressPrintingUpdates ? 'SET ADDRESS DEFAULTS' : 'VEHICLES'}`, 'BACK'],
      contentComponent: (
        <ContactsForm
          trigger={trigger}
          control={control}
          errors={errors}
          watch={watch}
          getValues={getValues}
          setValue={setValue}
          onShowComplianceInfo={index => {
            handleShowCI(index)
            setInitialStep(isUpdate ? 2 : 3)
          }}
        />
      ),
    },
    {
      label: 'Set Address Defaults',
      showStepper: true,
      stepOptional: true,
      footerButtonTitles: ['NEXT: VEHICLES', 'BACK'],
      contentComponent: <AddressDefaultsForm control={control} errors={errors} watch={watch} />,
    },
    {
      label: 'Vehicles',
      showStepper: true,
      stepOptional: true,
      footerButtonTitles: ['NEXT: FINANCIAL INFORMATION', 'BACK'],
      contentComponent: <VehiclesForm control={control} errors={errors} watch={watch} />,
    },
    {
      label: 'Financial Information',
      showStepper: true,
      stepOptional: true,
      footerButtonTitles: ['FINISH', 'BACK'],
      contentComponent: <FinancialInformationForm />,
    },
  ]
  const supplierSteps = [
    {
      label: 'General Information',
      showStepper: true,
      stepOptional: false,
      shouldCheckBuyEligibility: true,
      showEarlySubmitButton: true,
      footerButtonTitles: ['NEXT: ADDITIONAL INFORMATION', 'BACK TO START'],
      contentComponent: (
        <SupplierForm
          fullView={type !== 'supplier'}
          contactIndex={contactIndex || '0'}
          watch={watch}
          control={control}
          errors={errors}
          methods={methods}
          autoOpenIdModal={!licenseData}
        />
      ),
    },
    {
      label: 'Additional Information',
      showStepper: true,
      stepOptional: false,
      showEarlySubmitButton: true,
      footerButtonTitles: ['NEXT: VEHICLES', 'BACK'],
      contentComponent: (
        <CustomerAdditionalInformationForm control={control} errors={errors} contactIndex={contactIndex || '0'} />
      ),
    },
    {
      label: 'Vehicles',
      showStepper: true,
      stepOptional: true,
      footerButtonTitles: ['FINISH', 'BACK'],
      contentComponent: <VehiclesForm control={control} errors={errors} watch={watch} />,
    },
  ]
  const carrierSteps = [
    {
      label: 'General Information',
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: ['NEXT: LOCATIONS', 'BACK TO START'],
      contentComponent: (
        <CarrierGeneralInfoForm driversOptionList={driversOptionList} control={control} errors={errors} />
      ),
    },
    {
      label: 'Locations',
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: ['NEXT: CONTACTS', 'BACK'],
      contentComponent: (
        <LocationsInformationForm
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
        />
      ),
    },
    {
      label: 'Contacts',
      showStepper: true,
      stepOptional: false,
      footerButtonTitles: ['NEXT: FINANCIAL INFORMATION', 'BACK'],
      contentComponent: (
        <ContactsForm control={control} errors={errors} watch={watch} getValues={getValues} setValue={setValue} />
      ),
    },
    {
      label: 'Financial Information',
      showStepper: true,
      stepOptional: true,
      footerButtonTitles: ['FINISH', 'BACK'],
      contentComponent: <FinancialInformationForm />,
    },
  ]
  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const onSubmit = async data => {
    if (!isUpdate) {
      try {
        const customer = formatData(data)

        // Step 1: Create the customer
        const customerResponse = await createCustomer({ customer }).unwrap()
        const { id: customerId, customerCommonIdentifierString } = customerResponse

        // Step 2: Call saveGsPayVendor mutation with the new contact ID
        if (data.gsPayContactId) {
          try {
            handleSaveGsPayVendor(data.gsPayContactId)
          } catch (error) {
            console.error('Error saving GreenSpark Pay vendor:', error)
          }
        }

        // Handle success
        handleSuccess()
        trackEvent(isCarrier ? cz.CREATE_CARRIER : cz.CREATE_CUSTOMER)
        onHideModal({ hideAll })

        // Dispatch Redux actions
        dispatch(
          isCarrier
            ? setCarrier({ id: customerId, customerCommonIdentifierString })
            : setCustomer({ id: customerId, customerCommonIdentifierString }),
        )
        dispatch(clearContact())
      } catch (error) {
        handleError(error)
      }
    }
  }

  function onValidateCustomer(data) {
    onSubmit(data)
  }

  const isLoading =
    isCreatingCustomer ||
    isGettingData ||
    isUpdating ||
    isLoadingArchiveCustomer ||
    isSavingGsPayVendor ||
    isLoadingCustomers ||
    isFetchingAvailableDriversData

  const validateDirtyFields = step => {
    const {
      companyName,
      supplierTypeId,
      vendorClassId,
      priceSheetId,
      accountManagerGsUserId,
      notes,
      canBuy,
      locations,
      contacts,
      vehicles,
      gsPayContactId,
      preferredPaymentMethod,
      defaultPaymentTermId,
      creditBalance,
      defaultCurrency,
      taxNumber,
      taxRate,
      accountingNumber,
      accountType: accType,
      defaultCorporateLocation,
      defaultShippingLocation,
      defaultPayToLocation,
      defaultPayToContact,
      driverIds,
    } = dirtyFields
    if (isCarrier) {
      switch (step) {
        case 0:
          return !(companyName || notes || accType || driverIds)
        case 1:
          return !locations
        case 2:
          return !contacts
        case 3:
          return !(
            preferredPaymentMethod ||
            gsPayContactId ||
            defaultPaymentTermId ||
            creditBalance ||
            defaultCurrency ||
            taxNumber ||
            taxRate ||
            accountingNumber
          )
        default:
          return true
      }
    } else {
      switch (step) {
        case 0:
          return !(
            companyName ||
            supplierTypeId ||
            vendorClassId ||
            priceSheetId ||
            accountManagerGsUserId ||
            notes ||
            canBuy ||
            accType
          )
        case 1:
          return !locations
        case 2:
          return !contacts
        case 3:
          return enableAddressPrintingUpdates
            ? !(defaultCorporateLocation || defaultShippingLocation || defaultPayToLocation || defaultPayToContact)
            : !vehicles
        case 4:
          return enableAddressPrintingUpdates
            ? !vehicles
            : !(
                preferredPaymentMethod ||
                gsPayContactId ||
                defaultPaymentTermId ||
                creditBalance ||
                defaultCurrency ||
                taxNumber ||
                taxRate ||
                accountingNumber
              )
        case 5:
          return enableAddressPrintingUpdates
            ? !(
                preferredPaymentMethod ||
                gsPayContactId ||
                defaultPaymentTermId ||
                creditBalance ||
                defaultCurrency ||
                taxNumber ||
                taxRate ||
                accountingNumber
              )
            : true
        default:
          return true
      }
    }
  }

  const validateFields = async step => {
    let fields = []
    switch (step) {
      case 0:
        fields = ['accountType']
        break
      case 1:
        fields = isCarrier
          ? ['companyName', 'notes', 'driverIds']
          : [
              'companyName',
              'supplierTypeId',
              'vendorClassId',
              'priceSheetId',
              'accountManagerGsUserId',
              'notes',
              'canBuy',
            ]
        if (!isUpdate) updateCustomersFilter()
        break
      case 2:
        fields = ['locations']
        break
      case 3:
        fields = ['contacts']
        break
      case 4:
        if (isCarrier || !enableAddressPrintingUpdates) {
          fields = null
        } else {
          fields = [
            'defaultCorporateLocation',
            'defaultShippingLocation',
            'defaultPayToLocation',
            'defaultPayToContact',
          ]
        }
        break
      default:
        fields = null
    }
    const result = await trigger(fields)
    return result
  }

  const validateSupplierFields = async step => {
    const index = 0
    const contacts = getValues('contacts')
    const supplier = contacts[index]
    const { firstNameLegal, lastNameLegal } = supplier
    let fields = []
    switch (step) {
      case 0:
        setValue(`contacts.${index}.firstName`, firstNameLegal)
        setValue(`contacts.${index}.lastName`, lastNameLegal)
        fields = ['contacts']
        if (!isUpdate) updateCustomersFilter()
        break
      case 1:
        fields = ['vendorClassId', 'priceSheetId', 'accountManagerGsUserId', 'notes', 'contact']
        break
      default:
        fields = null
    }
    const result = await trigger(fields)
    return result
  }

  const handleValidatePartial = activeStep => {
    if (isUpdate) {
      // return true = save button disabled
      // return false = save button enabled
      if (!isDirty) return true
      return validateDirtyFields(activeStep)
    }
    // validation for supplier creation
    if (type === 'supplier') return validateSupplierFields(activeStep)
    // validation for customer creation
    return validateFields(activeStep)
  }

  const handleSubmitPartial = async activeStep => {
    if (isValid) {
      try {
        const customerUpdated = getValues()
        let customer = formatData(customerUpdated)
        const originalCustomerSanitized = formatData(originalCustomer)

        // Handle GS Pay vendor if needed
        const contactId = getValues('gsPayContactId')
        if (contactId && (!originalCustomer || originalCustomer.gsPayContactId !== contactId)) {
          const vendorResponse = await handleSaveGsPayVendor(contactId)
          if (vendorResponse) {
            customer.gsPayContactId = contactId
          }
        }

        const {
          companyName,
          supplierTypeId,
          vendorClassId,
          priceSheetId,
          accountManagerGsUserId,
          isSupplier: valueIsSupplier,
          isConsumer: valueIsConsumer,
          isCarrier: valueIsCarrier,
          notes,
          locations,
          contacts,
          vehicles,
          buysForbidden,
          preferredPaymentMethod,
          defaultPaymentTermId,
          creditBalance,
          defaultCurrency,
          taxNumber,
          taxRate,
          accountingNumber,
          driverIds,
        } = customer

        if (isCarrier) {
          switch (activeStep) {
            case 0:
              customer = {
                ...originalCustomerSanitized,
                companyName,
                notes,
                driverIds,
                customerCommonIdentifierString: companyName,
                isCarrier: valueIsCarrier,
              }
              break
            case 1:
              customer = {
                ...originalCustomerSanitized,
                locations,
              }
              break
            case 2:
              customer = {
                ...originalCustomerSanitized,
                contacts,
              }
              break
            case 3:
              customer = {
                ...originalCustomerSanitized,
                preferredPaymentMethod,
                defaultPaymentTermId,
                creditBalance,
                defaultCurrency,
                taxNumber,
                taxRate,
                accountingNumber,
              }
              break
            default:
              customer = originalCustomerSanitized
          }
        } else {
          switch (activeStep) {
            case 0:
              customer = {
                ...originalCustomerSanitized,
                companyName,
                supplierTypeId,
                customerCommonIdentifierString: companyName,
                vendorClassId,
                priceSheetId: valueIsSupplier ? priceSheetId : null,
                accountManagerGsUserId,
                notes,
                buysForbidden,
                isConsumer: valueIsConsumer,
                isSupplier: valueIsSupplier,
                isCarrier: valueIsCarrier,
              }
              break
            case 1:
              customer = {
                ...originalCustomerSanitized,
                locations,
              }
              break
            case 2:
              customer = {
                ...originalCustomerSanitized,
                contacts,
                locations:
                  isDirtyFieldInContacts(dirtyFields, 'isPrimaryContact') ||
                  isDirtyFieldInContacts(dirtyFields, 'locationId')
                    ? locations
                    : originalCustomerSanitized.locations,
              }
              break
            case 3:
              customer = enableAddressPrintingUpdates
                ? { ...originalCustomerSanitized, locations, contacts }
                : { ...originalCustomerSanitized, vehicles }
              break
            case 4:
              customer = enableAddressPrintingUpdates
                ? { ...originalCustomerSanitized, vehicles }
                : {
                    ...originalCustomerSanitized,
                    preferredPaymentMethod,
                    defaultPaymentTermId,
                    creditBalance,
                    defaultCurrency,
                    taxNumber,
                    taxRate,
                    accountingNumber,
                  }
              break
            case 5:
              customer = enableAddressPrintingUpdates
                ? {
                    ...originalCustomerSanitized,
                    preferredPaymentMethod,
                    defaultPaymentTermId,
                    creditBalance,
                    defaultCurrency,
                    taxNumber,
                    taxRate,
                    accountingNumber,
                  }
                : originalCustomerSanitized
              break
            default:
              customer = originalCustomerSanitized
          }
        }

        await updateCustomer({ customer: { ...customer, id: customerId }, customerId })
          .unwrap()
          .then(() => {
            handleSuccess()
            trackEvent(cz.UPDATE_CUSTOMER)
            dispatch(clearContact())
            setOriginalCustomer(customer)
            if (closeAfterSave) onHideModal()
          })
          .catch(errorMsg => {
            trackEvent({
              eventName: cz.UPDATE_CUSTOMER,
              errorMsg,
            })
            handleError(errorMsg)
          })
      } catch (error) {
        handleError(error)
      }
    }
  }

  const requestCheckBuyEligibility = async () => {
    const contacts = getValues('contacts').map(formatContact)
    const { data: buyEligibilityData } = await checkBuyEligibility({ body: contacts })
    const doNotBuyContacts = buyEligibilityData?.filter(contact => !!contact.doNotBuy) || []
    if (doNotBuyContacts.length > 0) {
      const names = makeCommaSeparatedString(
        doNotBuyContacts.map(contact => `${capitalize(contact.firstName)} ${capitalize(contact.lastName)}`.trim()),
      )
      const isAre = doNotBuyContacts.length > 1 ? 'are' : 'is'
      const message = `Warning: Unable to ${isUpdate ? 'update' : 'create'} customer. ${names} ${isAre} registered on the Ohio
      "Do Not Buy" list and ${isAre} unable to sell to this yard`
      setHasDoNotBuyContacts(true)
      dispatchSnackbar(dispatch, 'error', message)
    } else {
      setHasDoNotBuyContacts(false)
    }
  }

  const handleArchiveClick = () => setIsArchiveModalUp(true)
  const handleArchiveCustomer = async () => {
    await archiveCustomer({ customerId })
      .unwrap()
      .then(() => {
        trackEvent(cz.ARCHIVE_CUSTOMER)
        navigate('/app/customers')
        onHideModal()
      })
      .catch(error => {
        const errorMsg = error?.data?.error || 'An error occurred archiving the customer'
        dispatch(
          setSnackBar({
            snackbarType: 'error',
            snackbarMessage: errorMsg,
          }),
        )
        trackEvent({
          eventName: cz.ARCHIVE_CUSTOMER,
          errorMsg,
        })
      })
  }

  const getSteps = () => {
    if (type === 'supplier') return supplierSteps
    if (isCarrier) return isUpdate ? carrierSteps : [...startStep, ...carrierSteps]
    const filteredSteps = steps.filter(step => {
      if (step.label === 'Set Address Defaults') return enableAddressPrintingUpdates
      return true
    })
    return isUpdate || ['salesOrder', 'purchaseOrder'].includes(type) ? filteredSteps : [...startStep, ...filteredSteps]
  }

  return (
    <FormProvider {...methods}>
      <AlertModal
        open={isArchiveModalUp}
        modalType="void"
        title="Archive Customer"
        question="Are you sure you want to archive this customer?"
        description="Archiving a customer is irreversible and you will need to contact GreenSpark support to undo this action."
        confirmLabel="YES, ARCHIVE THIS CUSTOMER"
        cancelLabel="GO BACK"
        onClose={() => setIsArchiveModalUp(false)}
        onSubmit={handleArchiveCustomer}
      />

      {/* Use our hook's modal component */}
      <DuplicateCustomerModal />

      {isLoading && <Loading />}
      {showCI ? (
        <ComplianceInformationForm
          data={getValues(`contacts.${contactIndex}`)}
          onClose={() => setShowCI(false)}
          onSave={data => {
            handleSaveCI(data)
            requestCheckBuyEligibility()
          }}
        />
      ) : (
        <form onSubmit={handleSubmit(onValidateCustomer)}>
          <WizardStepper
            steps={getSteps()}
            onHideModal={onHideModal}
            initialStep={initialStep}
            nonLinear={isUpdate}
            title={title}
            onValidatePartial={handleValidatePartial}
            onSubmitPartial={handleSubmitPartial}
            onEarlySubmit={handleSubmit(onValidateCustomer)}
            validationCheck
            canArchive
            handleArchiveClick={handleArchiveClick}
            requestCheckBuyEligibility={requestCheckBuyEligibility}
            isCheckingEligibility={isLoadingCheckBuyEligibility}
            hasDoNotBuyContacts={hasDoNotBuyContacts}
          />
        </form>
      )}
    </FormProvider>
  )
}

NewCustomerContainer.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

NewCustomerContainer.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default NewCustomerContainer
